import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

/**
 * Technical issues split by what fixing them costs.
 *
 * Mirrors `backend/src/services/technical-issue-rollup.ts`. Every judgement is
 * the server's: which issues are site-wide, how many pages they reach, and
 * which section they concentrate in. Angular reads `siteWideCodes` and filters;
 * it never decides that an issue is site-wide, because a page's own panel and
 * the domain section have to agree and only one of them can hold the rule.
 */

/** See the backend taxonomy: build = fix once, content = fix per page. */
export type TechnicalIssueOrigin = 'build' | 'content' | 'mixed';

export type TechnicalIssueSeverity = 'critical' | 'warning';

export interface SiteWideTechnicalIssue {
  /** The DataForSEO check name, or `resource:<normalised message>`. */
  code: string;
  title: string;
  origin: TechnicalIssueOrigin;
  severity: TechnicalIssueSeverity;
  /** One sentence naming the single fix. Null when the taxonomy has none. */
  fix: string | null;
  pageCount: number;
  /** 0–1 of analysed pages. The UI phrases it; the server states it. */
  pageShare: number;
  /** `/blog` when the issue clusters in one section — a proxy for a template. */
  concentratedIn: string | null;
  concentratedPageCount: number | null;
  samplePaths: string[];
}

export interface TechnicalRollupTotals {
  issueMentions: number;
  siteWideMentions: number;
  pageSpecificMentions: number;
  /** Pages whose entire technical list was site-wide — nothing to do per page. */
  pagesFullyExplained: number;
  pagesWithPageSpecific: number;
}

/**
 * A page-specific issue whose cause is site-wide.
 *
 * "Low content rate" is the case this exists for: only the pages whose text is
 * short enough get tipped under the 10% bar, so the symptom stays in each
 * page's own list — but what tips them is a shell every page carries, and a row
 * that does not say so tells the author to write more when the fix is to ship
 * less markup.
 *
 * `note` is the DOMAIN half of the sentence, composed server-side. The surface
 * holding a page adds the page half from the numbers it already has.
 */
export interface TechnicalSharedCause {
  code: string;
  note: string;
}

/** The measurement behind the shell finding. See the backend module for the rules. */
export interface MarkupShellOverhead {
  /** p05 of non-text bytes — what every page carries. */
  floorBytes: number;
  medianBytes: number;
  p95Bytes: number;
  /** `p95 / floor`. Near 1 means a constant shell rather than per-page weight. */
  spread: number;
  /** Null when page length does not vary, so the coefficient is undefined. */
  wordCorrelation: number | null;
  measuredPages: number;
  affectedPages: number;
  rescuedPages: number;
  verdict: 'shell' | 'mixed' | 'content';
}

export interface TechnicalRollup {
  domain: string;
  analysedPageCount: number;
  siteWide: SiteWideTechnicalIssue[];
  /** The hoisted codes, so one page's panel can make the same split locally. */
  siteWideCodes: string[];
  /** Keyed by issue code — the link lives in the payload, not in a component. */
  sharedCauses: TechnicalSharedCause[];
  shellOverhead: MarkupShellOverhead | null;
  totals: TechnicalRollupTotals;
}

@Injectable({ providedIn: 'root' })
export class TechnicalRollupService {
  private readonly API_URL = environment.apiUrl + '/v1';

  /**
   * One in-flight/settled request per domain.
   *
   * Cached, unlike `ImpactBriefService`, and for a reason that does not apply
   * there: every expanded package row mounts a `url-detail` that needs the same
   * domain's `siteWideCodes` to split its own list. Five open rows would
   * otherwise be five identical aggregations over four hundred pages. The
   * rollup is also far less volatile than a week plan — it only moves when a
   * crawl re-analyses pages, and a stale one degrades to showing an issue in a
   * page row that could have been hoisted, which is the pre-feature behaviour
   * rather than a wrong number.
   */
  private readonly cache = new Map<string, Observable<TechnicalRollup | null>>();

  constructor(private http: HttpClient) {}

  /**
   * The domain's rollup, or null when there is nothing to read.
   *
   * `domain` may be null, which lets the server resolve the workspace's
   * dashboard domain exactly as the impact brief does — the Dashboard has no
   * domain to pass before its first payload lands. Prefer passing the domain
   * the surface already shows.
   *
   * A failed read resolves to null: the split is an improvement on the page
   * lists, and losing it must degrade to the old undivided list rather than
   * take the section — or the expanded row it sits in — down with it.
   */
  getRollup(domain: string | null, options: { refresh?: boolean } = {}): Observable<TechnicalRollup | null> {
    const key = (domain || '').trim().toLowerCase();
    if (options.refresh) this.cache.delete(key);

    const cached = this.cache.get(key);
    if (cached) return cached;

    const params = key ? { domain: key } : undefined;
    const request = this.http
      .get<{ success: boolean; data: TechnicalRollup | null }>(
        `${this.API_URL}/urls/technical-rollup`,
        { params }
      )
      .pipe(
        map((res) => res?.data ?? null),
        catchError(() => of(null)),
        // A null answer is not worth caching for the session: it is usually a
        // transient failure, and the next surface to ask should get a real try.
        tap((payload) => {
          if (!payload) this.cache.delete(key);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this.cache.set(key, request);
    return request;
  }

  /** Drops every cached domain — for after a crawl completes. */
  clear(): void {
    this.cache.clear();
  }
}
